import type React from "react";
import { toast as toastNotify } from "react-toastify";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Snackbar,
  Alert,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  AddOutlined,
  ChevronLeft,
  ChevronRight,
  Close,
  DeleteOutline,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
  IContact,
  NewContactForm,
  VenueContact,
  VenueData,
} from "../../types/venues.types";
import AddContactModal from "../../view-venue/components/AddContactModal";
import wifi from "../../../../components/icons/wifi.png";
import power from "../../../../components/icons/power.png";
import capacity from "../../../../components/icons/capacity.png";
import room from "../../../../components/icons/mage_dashboard.png";
import PenPaperIcon from "../../../../components/icons/PenPaperIcon";

import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";
import { ScheduleSiteVisitModal, SiteVisitData } from "../site-visit";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../service/Api";
import {
  calculateNextSiteVisitDate,
  formatDisplayDate,
} from "../site-visit/ScheduleSiteVisitModal";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import VenueCardImage from "../../list-venues/VenueCardImage";
import AddOtherContactsModal from "./AddOtherContacts";
import VenuePublication from "./VenuePublication";

const venueTheme = createTheme({
  palette: {
    primary: { main: "#8B4513" },
    secondary: { main: "#c9302c" },
  },
  typography: { fontFamily: "inherit" },
});

type PaginatedResponse<T> = {
  results: T[];
};
// ─────────────────────────────────────────────
// Tab labels
// ─────────────────────────────────────────────
export const TABS = [
  "General",
  "Rooms",
  "Floor Plans",
  "Gallery",
  "Bookings",
  "Contacts",
] as const;

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────
export interface ViewVenueV2DashboardProps {
  // Layout state
  activeTab: number;
  onTabChange: (newTab: number) => void;
  notePage: number;
  onNotePrev: () => void;
  onNoteNext: () => void;
  contactCard: React.ReactNode;
  // Venue data
  venueData: VenueData;
  coverPhoto: string | null;
  healthScore: number;
  onNoteUpdate?: (note: string) => void;
  notes: string[];
  currentNote: string | null;
  totalBookings: number | undefined;
  formatDate: (date: string) => string;

  primaryContact: VenueContact | null;
  searchResults: IContact[];
  contactModalStep: "search" | "form";
  setContactModalStep: (step: "search" | "form") => void;
  newContactForm: NewContactForm;
  setNewContactForm: (form: NewContactForm) => void;
  // Tab content (built by the container)
  tabContent: Record<number, React.ReactNode>;

  // Navigation callbacks
  onEditClick: () => void;
  onSiteVisitClick: () => void;
  onEditNoteClick: () => void;

  // Contact sidebar actions
  onContactRemove: (id: number) => void;
  onAddContactClick: () => void;

  // Add Contact Modal
  openContactModal: boolean;
  onContactModalClose: () => void;

  contactSearch: string;
  setContactSearch: (v: string) => void;

  isSearching: boolean;
  savingContact: boolean;
  searchAttempted: boolean;
  onAddExistingContact: (contact: IContact) => Promise<void>;
  onPhotosChange?: () => void;
  onAddNewContact: () => void;
}

const ViewVenueV2Dashboard: React.FC<ViewVenueV2DashboardProps> = ({
  activeTab,
  onTabChange,
  notePage,
  onNotePrev,
  onNoteNext,
  venueData,
  coverPhoto,
  healthScore,
  notes,
  onNoteUpdate,
  contactCard,
  currentNote,
  totalBookings,
  formatDate,
  tabContent,
  onEditClick,
  openContactModal,
  onContactModalClose,
  contactModalStep,
  setContactModalStep,
  contactSearch,
  setContactSearch,
  searchResults,
  isSearching,
  savingContact,
  searchAttempted,
  newContactForm,
  setNewContactForm,
  onAddExistingContact,
  onAddNewContact,
  primaryContact,
  onPhotosChange,
}) => {
  const fullAddress = [
    venueData.address_one,
    venueData.address_two,
    venueData.suburb,
    venueData.city,
    venueData.country,
  ]
    .filter(Boolean)
    .join(", ");

  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editedNote, setEditedNote] = useState(currentNote || "");
  const [savingNote, setSavingNote] = useState(false);
  useEffect(() => {
    setEditedNote(currentNote || "");
  }, [currentNote]);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(false);
  const [siteVisitData, setSiteVisitData] = useState<SiteVisitData | null>(
    null,
  );
  const [openAddContactModal, setOpenAddContactModal] = useState(false);
  // ── Venue photo grid (General tab) hover actions ──
  const [galleryFiles, setGalleryFiles] = useState(venueData.files ?? []);
  useEffect(() => {
    setGalleryFiles(venueData.files ?? []);
  }, [venueData.files]);

  const [deletingPhotoId, setDeletingPhotoId] = useState<number | null>(null);
  const [settingCoverId, setSettingCoverId] = useState<number | null>(null);
  const [photoLightboxId, setPhotoLightboxId] = useState<number | null>(null);

  const venuePhotos = [
    ...(galleryFiles?.filter((f) => f.file_type === "venue_photos") ?? []),
  ].sort((a, b) => (a.is_cover === b.is_cover ? 0 : a.is_cover ? -1 : 1));

  const handleDeletePhoto = async (e: React.MouseEvent, fileId: number) => {
    e.stopPropagation();
    setDeletingPhotoId(fileId);
    try {
      await apiService.delete(`/api/venues/document/${fileId}/`);
      setGalleryFiles((prev) => prev.filter((f) => f.id !== fileId));
      if (photoLightboxId === fileId) {
        setPhotoLightboxId(null);
      }
      toastNotify.success("Photo deleted successfully");
      onPhotosChange?.();
    } catch (err) {
      console.error("Failed to delete photo", err);
      toastNotify.error("Failed to delete photo. Please try again.");
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleSetCoverPhoto = async (e: React.MouseEvent, fileId: number) => {
    e.stopPropagation();
    setSettingCoverId(fileId);
    try {
      await apiService.patch(`/api/venues/document/${fileId}/`, {
        is_cover: true,
      });
      setGalleryFiles((prev) =>
        prev.map((f) => ({ ...f, is_cover: f.id === fileId })),
      );
      toastNotify.success("Cover photo updated");
      onPhotosChange?.();
    } catch (err) {
      console.error("Failed to set cover photo", err);
      toastNotify.error("Failed to set cover photo. Please try again.");
    } finally {
      setSettingCoverId(null);
    }
  };

  const selectedPhoto =
    photoLightboxId !== null
      ? (venuePhotos.find((f) => f.id === photoLightboxId) ?? null)
      : null;

  const photoLightboxIndex = selectedPhoto
    ? venuePhotos.findIndex((f) => f.id === photoLightboxId)
    : null;

  const closePhotoLightbox = useCallback(() => setPhotoLightboxId(null), []);

  const goPhotoPrev = useCallback(() => {
    if (photoLightboxIndex === null || venuePhotos.length === 0) return;
    const prevIndex =
      photoLightboxIndex > 0 ? photoLightboxIndex - 1 : venuePhotos.length - 1;
    setPhotoLightboxId(venuePhotos[prevIndex].id);
  }, [photoLightboxIndex, venuePhotos]);

  const goPhotoNext = useCallback(() => {
    if (photoLightboxIndex === null || venuePhotos.length === 0) return;
    const nextIndex =
      photoLightboxIndex < venuePhotos.length - 1 ? photoLightboxIndex + 1 : 0;
    setPhotoLightboxId(venuePhotos[nextIndex].id);
  }, [photoLightboxIndex, venuePhotos]);
  const handleSaveNote = async () => {
    setSavingNote(true);

    try {
      await apiService.patch(`/api/venues/venue/${venueData.id}/`, {
        notes: editedNote,
      });

      showSuccessToast("Note updated successfully");

      // ✅ THIS updates the UI properly
      onNoteUpdate?.(editedNote);

      setIsEditingNote(false);
    } catch (error) {
      let errorMessage = "Failed to update note";

      const errorResponse = (
        error as {
          response?: {
            status?: number;
            data?: {
              detail?: string;
              errors?: Record<string, unknown>;
            };
          };
        }
      )?.response;
      if (errorResponse?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }

      showErrorToast(errorMessage);
    } finally {
      setSavingNote(false);
    }
  };
  const fetchSiteVisitStatus = useCallback(async () => {
    try {
      const res = await apiService.get<PaginatedResponse<SiteVisitData>>(
        `/api/venues/v1/site-visit/?venue=${venueData.id}`,
      );
      setSiteVisitData(res.data.results?.[0] ?? null);
    } catch (err) {
      console.error(err);
    }
  }, [venueData.id]);

  useEffect(() => {
    fetchSiteVisitStatus();
  }, [fetchSiteVisitStatus]);
  const isAssigned = siteVisitData?.status === "assigned";

  const nextSiteVisitDate =
    isAssigned && siteVisitData?.scheduled_date_time
      ? calculateNextSiteVisitDate(siteVisitData.scheduled_date_time)
      : null;

  const handleSuccess = () => {
    setToast(true);
    fetchSiteVisitStatus();
  };

  console.log("contacts:", venueData.contacts);
  return (
    <ThemeProvider theme={venueTheme}>
      <Box
        sx={{
          width: "100%",
        }}
      >
        {/* Main content: left + right sidebar */}
        <Box sx={{ display: "flex", p: 2, gap: 2, alignItems: "stretch" }}>
          {/* ── Left ─────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Hero Image */}

            <Box
              sx={{
                position: "relative",
                borderRadius: 3,
                overflow: "hidden",
                height: 180,
                "&:hover .health-card": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              }}
            >
              <VenueCardImage
                imageUrl={coverPhoto}
                venueName={venueData.name}
                height={180}
              />
              {/* Venue name + type overlay — bottom left */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                  color: "white",
                }}
              >
                {venueData.venue_type && (
                  <Chip
                    label={venueData.venue_type}
                    size="small"
                    sx={{
                      bgcolor: "#4CAF50",
                      color: "white",
                      fontSize: 10,
                      height: 20,
                      mb: 0.5,
                    }}
                  />
                )}
                <Typography variant="h6" fontWeight={700} fontSize="20px">
                  {venueData.name}
                </Typography>
                <Typography variant="body2" fontSize="14px" fontWeight={600}>
                  {fullAddress}
                </Typography>
              </Box>

              {/* Health score badge — top right, always visible */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: healthScore >= 75 ? "#4CAF50" : "#FF9800",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                }}
              >
                {healthScore}%
              </Box>

              {/* Edit button — top left */}
              <IconButton
                size="small"
                onClick={onEditClick}
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  bgcolor: "rgba(255,255,255,0.85)",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                <PenPaperIcon sx={{ fontSize: 18 }} />
              </IconButton>

              {/* Glassmorphism health card — bottom right, revealed on hover */}
              <Box
                className="health-card"
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  width: 160,
                  backdropFilter: "blur(10px)",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: 2,
                  p: 1.5,
                  color: "white",
                  opacity: 0.35,
                  transform: "translateY(8px)",
                  transition: "opacity 0.3s ease, transform 0.3s ease",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    mb: 1,
                  }}
                >
                  Venue Health
                </Typography>

                {/* Data Completeness + LinearProgress */}
                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.62rem", opacity: 0.8 }}>
                      Data Completeness
                    </Typography>
                    <Typography sx={{ fontSize: "0.62rem", fontWeight: 700 }}>
                      {healthScore}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={healthScore}
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.2)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor:
                          healthScore < 50
                            ? "#ef4444"
                            : healthScore < 75
                              ? "#3b82f6"
                              : "#22c55e",
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    borderTop: "1px solid rgba(255,255,255,0.15)",
                    my: 0.75,
                  }}
                />

                {/* Last Updated */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>
                    Last updated:
                  </Typography>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 600 }}>
                    {venueData.updated_at
                      ? formatDate(venueData.updated_at)
                      : "—"}
                  </Typography>
                </Box>

                {/* Past Events */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ fontSize: "0.62rem", opacity: 0.7 }}>
                    Past Events
                  </Typography>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 600 }}>
                    {totalBookings ?? "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                px: 2,
                py: 1,
                borderRadius: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: reduxTheme.primary_colors?.[900] || "background.paper",
              }}
            >
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={wifi}
                    sx={{ width: 20, height: 20 }}
                  />
                }
                label="INTERNET"
                value={venueData.internet_service_provider || "—"}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={power}
                    sx={{ width: 20, height: 20 }}
                  />
                }
                label="POWER"
                value={venueData.power_type || "—"}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={capacity}
                    sx={{ width: 20, height: 20 }}
                  />
                }
                label="CAPACITY"
                value={venueData.capacity?.toString() || "—"}
              />
              <StatItem
                icon={
                  <Box
                    component="img"
                    src={room}
                    sx={{ width: 20, height: 20 }}
                  />
                }
                label="ROOMS"
                value={venueData.rooms?.length?.toString() || "—"}
              />
            </Paper>
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                borderRadius: 3,
                p: 2,
                bgcolor: "background.paper",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => onTabChange(v)}
                variant="fullWidth"
                scrollButtons="auto"
                sx={{
                  mt: 2,
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontSize: 13,
                    px: 2,
                  },
                }}
              >
                {TABS.map((label) => (
                  <Tab key={label} label={label} />
                ))}
              </Tabs>

              {/* Animated Tab Content */}
              <Box sx={{ mt: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor:
                      reduxTheme.primary_colors?.[800] || "background.paper",
                    minHeight: 200,
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      {activeTab === 0 ? (
                        // ── General tab: original two-column layout ──
                        <Box sx={{ display: "flex", gap: 2 }}>
                          {/* General Information card */}
                          <Paper
                            variant="outlined"
                            sx={{
                              flex: 1,
                              p: 2,
                              borderRadius: 2,
                              minWidth: 200,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 2,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                General Information
                              </Typography>
                              <IconButton size="small" onClick={onEditClick}>
                                <PenPaperIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            <InfoRow
                              label="Venue Name"
                              value={venueData.name || "—"}
                            />
                            <InfoRow
                              label="Venue Type"
                              value={venueData.venue_type || "—"}
                            />
                            <InfoRow
                              label="Capacity"
                              value={venueData.capacity?.toString() || "—"}
                            />
                            <InfoRow
                              label="Rooms"
                              value={
                                venueData.rooms?.length?.toString() || "none"
                              }
                            />
                            <InfoRow
                              label="Website"
                              value={
                                venueData.website ? (
                                  <a
                                    href={venueData.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "blue",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {venueData.website}
                                  </a>
                                ) : (
                                  "—"
                                )
                              }
                            />
                            <InfoRow
                              label="Instagram"
                              value={
                                venueData.instagram ? (
                                  <a
                                    href={venueData.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "blue",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {venueData.instagram}
                                  </a>
                                ) : (
                                  "—"
                                )
                              }
                            />
                            <InfoRow
                              label="Facebook"
                              value={
                                venueData.facebook ? (
                                  <a
                                    href={venueData.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "blue",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {venueData.facebook}
                                  </a>
                                ) : (
                                  "—"
                                )
                              }
                            />
                            <InfoRow
                              label="Address"
                              value={fullAddress || "—"}
                            />
                            <Box
                              sx={{
                                mt: 2,
                                height: 100,
                                bgcolor: "background.paper",
                                borderRadius: 2,
                              }}
                            />
                          </Paper>

                          {/* Gallery grid */}
                          {/* Gallery grid */}
                          <Box sx={{ flex: 1.5 }}>
                            {(() => {
                              const displayPhotos = venuePhotos.slice(0, 9);
                              const remaining = venuePhotos.length - 9;

                              return (
                                <Grid container spacing={1}>
                                  {displayPhotos.map((file, index) => (
                                    <Grid
                                      key={file.id ?? index}
                                      size={{ xs: index === 0 ? 6 : 3 }}
                                    >
                                      <Box
                                        onClick={() =>
                                          setPhotoLightboxId(file.id)
                                        }
                                        sx={{
                                          position: "relative",
                                          height: index === 0 ? 120 : 80,
                                          borderRadius: 2,
                                          overflow: "hidden",
                                          backgroundImage: `url(${file.file})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                          cursor: "pointer",
                                          "&:hover .photo-delete-btn": {
                                            opacity: 1,
                                          },
                                          "&:hover .photo-cover-btn": {
                                            opacity: 1,
                                          },
                                        }}
                                      >
                                        {file.is_cover && (
                                          <Chip
                                            label="Cover"
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              left: 8,
                                              bgcolor: "rgba(255,0,0,0.8)",
                                              color: "white",
                                              fontSize: 10,
                                              height: 20,
                                              "& .MuiChip-label": { px: 1 },
                                            }}
                                          />
                                        )}

                                        {/* Delete button — top right */}
                                        <IconButton
                                          className="photo-delete-btn"
                                          onClick={(e) =>
                                            handleDeletePhoto(e, file.id)
                                          }
                                          disabled={deletingPhotoId === file.id}
                                          size="small"
                                          sx={{
                                            position: "absolute",
                                            top: 4,
                                            right: 4,
                                            opacity: 0,
                                            transition: "opacity 0.2s",
                                            bgcolor: "rgba(0,0,0,0.55)",
                                            color: "#fff",
                                            padding: "3px",
                                            "&:hover": {
                                              bgcolor: "rgba(211,47,47,0.85)",
                                            },
                                            "&.Mui-disabled": {
                                              opacity: 0.5,
                                              color: "#fff",
                                            },
                                          }}
                                        >
                                          {deletingPhotoId === file.id ? (
                                            <CircularProgress
                                              size={12}
                                              color="inherit"
                                            />
                                          ) : (
                                            <DeleteOutline
                                              sx={{ fontSize: 14 }}
                                            />
                                          )}
                                        </IconButton>

                                        {/* Set-cover button — bottom right (only when not already cover) */}
                                        {!file.is_cover && (
                                          <IconButton
                                            className="photo-cover-btn"
                                            onClick={(e) =>
                                              handleSetCoverPhoto(e, file.id)
                                            }
                                            disabled={
                                              settingCoverId === file.id
                                            }
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              bottom: 4,
                                              right: 4,
                                              opacity: 0,
                                              transition: "opacity 0.2s",
                                              bgcolor: "rgba(0,0,0,0.55)",
                                              color: "#fff",
                                              padding: "3px",
                                              "&:hover": {
                                                bgcolor: "rgba(76,175,80,0.9)",
                                              },
                                              "&.Mui-disabled": {
                                                opacity: 0.5,
                                                color: "#fff",
                                              },
                                            }}
                                          >
                                            {settingCoverId === file.id ? (
                                              <CircularProgress
                                                size={12}
                                                color="inherit"
                                              />
                                            ) : (
                                              <StarBorder
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                          </IconButton>
                                        )}

                                        {index === 8 && remaining > 0 && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              inset: 0,
                                              bgcolor: "rgba(0,0,0,0.45)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              fontSize={11}
                                              fontWeight={700}
                                              color="white"
                                            >
                                              +{remaining} more
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>
                                  ))}
                                  {venuePhotos.length === 0 && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box
                                        sx={{
                                          height: 120,
                                          borderRadius: 2,
                                          bgcolor: "action.hover",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.disabled"
                                        >
                                          No photos uploaded yet
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              );
                            })()}
                          </Box>
                        </Box>
                      ) : (
                        tabContent[activeTab]
                      )}
                    </motion.div>
                  </AnimatePresence>
                </Paper>
              </Box>
            </Paper>
          </Box>
          {/* ── Right Sidebar ─────────────────── */}
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {" "}
            {/* Website Listing */}
            <VenuePublication
              key={venueData.id}
              venueData={{
                ...venueData,
                files: galleryFiles,
              }}
              onEditVenue={onEditClick}
            />
            {/* Primary Contact Card */}
            <Box
              sx={{
                mt: 2,
                height: 180,
                overflow: "hidden",
                borderRadius: 2,
                borderColor:
                  reduxTheme.primary_colors?.[800] || "background.paper",
              }}
            >
              {contactCard}
            </Box>{" "}
            {/* Other Contacts */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Other Contacts
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setOpenAddContactModal(true)}
                  sx={{
                    bgcolor: "#4CAF50",
                    color: "white",
                    width: 22,
                    height: 22,
                    "&:hover": { bgcolor: "#388E3C" },
                  }}
                >
                  <AddOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>

              {venueData.contacts &&
              venueData.contacts.filter((c) => c.id !== primaryContact?.id)
                .length > 0 ? (
                venueData.contacts
                  .filter((c) => c.id !== primaryContact?.id)
                  .map((contact) => (
                    <ContactRow
                      key={contact.id}
                      contact={contact}
                      onContactClick={() => onTabChange(5)}
                    />
                  ))
              ) : (
                <Typography variant="caption" color="text.disabled">
                  No other contacts.
                </Typography>
              )}
            </Paper>
            {/* Venue Health */}
            {/* <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Venue Health
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Data Completeness
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {healthScore}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={healthScore}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        healthScore < 50
                          ? "#ef4444" // red
                          : healthScore < 75
                            ? "#3b82f6" // blue
                            : "#22c55e", // green
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Last updated:
                </Typography>
                <Typography variant="caption">
                  {venueData.updated_at
                    ? formatDate(venueData.updated_at)
                    : "—"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Past Events
                </Typography>
                <Typography variant="caption">
                  {totalBookings ?? "—"}
                </Typography>
              </Box>
            </Paper> */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                mt: 2,
              }}
            >
              {isAssigned ? (
                <Box>
                  {/* STATUS BADGE */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 10,
                      bgcolor: "rgba(46, 125, 50, 0.12)",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      fontSize="0.75rem"
                      fontWeight={600}
                      color="success.main"
                    >
                      ✓ Site Visit Scheduled
                    </Typography>
                  </Box>

                  {/* DETAILS */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Scheduled Date
                    </Typography>

                    <Typography fontWeight={600}>
                      {siteVisitData?.scheduled_date_time
                        ? new Date(
                            siteVisitData.scheduled_date_time,
                          ).toLocaleDateString(undefined, {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "N/A"}
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Next Site Visit Due
                      </Typography>

                      <Typography fontWeight={700} color="warning.main">
                        {nextSiteVisitDate
                          ? formatDisplayDate(nextSiteVisitDate.toISOString())
                          : "Not scheduled"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    No site visit scheduled yet
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setOpen(true)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.2,
                      background: reduxTheme.primary_colors[400],
                      boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
                      "&:hover": {
                        boxShadow: "0 6px 18px rgba(25,118,210,0.4)",
                      },
                    }}
                  >
                    Schedule Site Visit
                  </Button>
                </Box>
              )}
            </Paper>
            {/* Notes */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                mt: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Notes
                </Typography>
                <IconButton size="small" onClick={() => setIsEditingNote(true)}>
                  <PenPaperIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-start",
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {isEditingNote ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={editedNote}
                    onChange={(e) => setEditedNote(e.target.value)}
                    variant="standard"
                    autoFocus
                    InputProps={{ disableUnderline: true }}
                  />
                ) : currentNote ? (
                  <Typography variant="caption">{currentNote}</Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    No notes added yet.
                  </Typography>
                )}
              </Box>
              {isEditingNote && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setIsEditingNote(false);
                      setEditedNote(currentNote || "");
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={savingNote}
                    onClick={handleSaveNote}
                  >
                    {savingNote ? "Saving..." : "Save"}
                  </Button>
                </Box>
              )}
              {notes.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {notePage + 1}/{notes.length}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      disabled={notePage === 0}
                      onClick={onNotePrev}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={notePage === notes.length - 1}
                      onClick={onNoteNext}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      <AddOtherContactsModal
        open={openAddContactModal}
        onClose={() => setOpenAddContactModal(false)}
        savingContact={savingContact}
        newContactForm={newContactForm}
        setNewContactForm={setNewContactForm}
        onAddExistingContact={onAddExistingContact}
        onAddNewContact={() => {
          onAddNewContact();
          setOpenAddContactModal(false);
        }}
      />
      {/* Add Contact Modal */}
      <AddContactModal
        open={openContactModal}
        onClose={onContactModalClose}
        contactModalStep={contactModalStep}
        setContactModalStep={setContactModalStep}
        contactSearch={contactSearch}
        setContactSearch={setContactSearch}
        searchResults={searchResults}
        isSearching={isSearching}
        savingContact={savingContact}
        searchAttempted={searchAttempted}
        newContactForm={newContactForm}
        setNewContactForm={setNewContactForm}
        onAddExistingContact={onAddExistingContact}
        onAddNewContact={onAddNewContact}
      />

      <ScheduleSiteVisitModal
        open={open}
        onClose={() => setOpen(false)}
        venue={venueData}
        venueId={venueData.id}
        onSuccess={handleSuccess}
      />

      {/* ── Photo Lightbox (General tab gallery) ── */}
      <Dialog
        open={photoLightboxIndex !== null}
        onClose={closePhotoLightbox}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: reduxTheme.primary_colors?.[900] || "background.paper",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Venue Photo
            </Typography>
            {photoLightboxIndex !== null && (
              <Typography variant="caption" color="text.secondary">
                {photoLightboxIndex + 1} / {venuePhotos.length}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {selectedPhoto && (
              <Button
                size="small"
                startIcon={selectedPhoto.is_cover ? <Star /> : <StarBorder />}
                disabled={
                  selectedPhoto.is_cover || settingCoverId === selectedPhoto.id
                }
                onClick={(e) => handleSetCoverPhoto(e, selectedPhoto.id)}
                sx={{ textTransform: "none" }}
              >
                {selectedPhoto.is_cover ? "Cover Photo" : "Set as Cover"}
              </Button>
            )}
            <IconButton onClick={closePhotoLightbox} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{ p: 0, position: "relative", bgcolor: "rgba(0,0,0,0.04)" }}
        >
          {selectedPhoto && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
              }}
            >
              {venuePhotos.length > 1 && (
                <IconButton
                  onClick={goPhotoPrev}
                  sx={{
                    position: "absolute",
                    left: 12,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  }}
                >
                  <ChevronLeft />
                </IconButton>
              )}
              <Box
                component="img"
                src={selectedPhoto.file}
                alt={`photo-${selectedPhoto.id}`}
                sx={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              {venuePhotos.length > 1 && (
                <IconButton
                  onClick={goPhotoNext}
                  sx={{
                    position: "absolute",
                    right: 12,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  }}
                >
                  <ChevronRight />
                </IconButton>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast}
        autoHideDuration={3000}
        onClose={() => setToast(false)}
      >
        <Alert severity="success" variant="filled">
          Site visit scheduled successfully
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default ViewVenueV2Dashboard;

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        mb: 0.75,
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          minWidth: "fit-content",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          flex: 1,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function StatItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: 1,
          bgcolor: "background.paper",
          color: "text.secondary",
        }}
      >
        {icon}
      </Box>

      {/* Text */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: 9,
            color: "text.secondary",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}
function ContactRow({
  contact,
  onContactClick,
}: {
  contact: VenueContact;
  onContactClick?: () => void;
}) {
  const displayName =
    `${contact.first_name} ${contact.last_name}`.trim() || "Unknown";
  const role = contact.role || "—";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <Box
      onClick={onContactClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        py: 0.75,
        px: 1,
        borderRadius: 2,
        transition: "background 0.15s",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          bgcolor: "#e8f0fe",
          color: "#1a73e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.65rem",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>

      {/* Name + Role */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#222",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayName}
        </Typography>
        <Typography sx={{ fontSize: "0.67rem", color: "#999", lineHeight: 1 }}>
          {role}
        </Typography>
      </Box>

      <ChevronRight sx={{ fontSize: 14, color: "#ccc", flexShrink: 0 }} />
    </Box>
  );
}
